import {
  faFile,
  faFolder,
  faPlusCircle
} from "@fortawesome/pro-light-svg-icons";
import { faCaretLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import DefaultFileSystem from "../../js/DefaultFileSystem";
import usePersistentState from "../../js/usePersistentState";
import Panel from "../Panel";
import ToastWithUndo from "../ToastWithUndo";
import FilePanelSection from "./FilePanelSection";

function findNewFilename(baseName, fileList) {
  const doesFileExist = fn => fileList.some(file => file.name === fn);
  let fileName = baseName,
    count = 1;
  while (doesFileExist(fileName)) {
    fileName = `${baseName} ${count++}`;
  }
  return fileName;
}
/**
 * filters out a file from a file list
 * @param  {{path, name}} file     the file to filter out
 * @param  {[file...]} fileList the list of files to filter
 * @return {[file...]}          the same list of files, without {file}
 */
const filterOutFileFromFileList = (file, fileList) =>
  fileList.filter(f => !(f.path === file.path && f.name === file.name));
/**
 * Filters out an entire path from a list of files
 * @param  {[type]} file             [description]
 * @param  {[type]} fileList         [description]
 * @param  {[type]} returnComplement if true, return only the files that were in that path, rather than only those that weren't
 * @return {[type]}                  [description]
 */
const filterOutFolderFromFileList = (file, fileList, returnComplement) => {
  const path = file.path + file.name + "/",
    negateIfNecessary = returnComplement ? bool => bool : bool => !bool;
  return fileList.filter(f =>
    negateIfNecessary(
      (f.path === file.path && f.name === file.name) || f.path.startsWith(path)
    )
  );
};
function noop() {}

function FileManagementPanel(props) {
  const [fileList, setFileList] = usePersistentState(
      "fileList",
      DefaultFileSystem.getFileList
    ),
    onSelectFile = props.onSelectFile || noop,
    renameTextControlRef = useRef(),
    [activePath, setActivePath] = useState("/"),
    [renameFileModalState, setRenameFileModalState] = useState({
      isShowing: false
    }),
    [undoToast, setUndoToast] = useState(null),
    { selectedFilePath } = props,
    setSelectedFile = useCallback(
      file => {
        const filePath = file.path + file.name;
        if (filePath !== selectedFilePath) {
          // update the lastAccessed time in the file list:
          setFileList(fileList =>
            fileList.map(f =>
              f.path + f.name === filePath
                ? { ...f, lastAccessed: new Date().getTime() }
                : f
            )
          );
          onSelectFile(file);
        }
      },
      [onSelectFile, selectedFilePath, setFileList]
    ),
    handleRenameFileModalClose = () =>
      setRenameFileModalState(state => ({ ...state, isShowing: false })),
    handleRenameFile = () => {
      DefaultFileSystem.renameFile(
        renameFileModalState.file,
        renameFileModalState.fileName
      );
      setRenameFileModalState(state => ({ ...state, isShowing: false }));
      setFileList(fileList => [...fileList]);
    },
    selectParentPath = () => {
      const match = activePath.match(/^(\/.*\/)[^/]*\/$/),
        newPath = match ? match[1] : "/";
      setActivePath(newPath);
    },
    filePanelProps = {
      selectedFilePath,
      onSelectFile: file => {
        if (file.isFolder) {
          setActivePath(file.path + file.name + "/");
        } else {
          setSelectedFile(file);
        }
      },
      onRenameFile: file => {
        setRenameFileModalState({ isShowing: true, file, fileName: file.name });
        console.info("onRenameFile", file);
      },
      onRemoveFile: file => {
        const message = file.isFolder
            ? `Folder '${file.name}' and its contents were deleted`
            : `File '${file.name}' was deleted`,
          key = file.path + file.name + (file.isFolder ? "/" : ""),
          closeThisToast = () => setUndoToast(null);
        let undoFiles = [file];
        if (file.isFolder) {
          undoFiles = filterOutFolderFromFileList(file, fileList, true);
          setFileList(fileList => filterOutFolderFromFileList(file, fileList));
        } else {
          setFileList(fileList => filterOutFileFromFileList(file, fileList));
        }
        const undoThisDelete = () => {
          setFileList(fileList => fileList.concat(undoFiles));
          if (!file.isFolder) {
            setSelectedFile(file);
          }
          closeThisToast();
        };
        setUndoToast({
          key,
          message,
          show: true,
          onClose: closeThisToast,
          onUndo: undoThisDelete
        });
      },
      onCopyFile: file => {
        // TODO: fileList needs to be constructed based on the path of the file object
        let name = findNewFilename(file.name.replace(/\s+\d+$/, ""), fileList),
          newFile = { path: file.path, name };
        DefaultFileSystem.copyFile(file, name);
        setFileList(fileList.concat(newFile));
        setSelectedFile(newFile);
      },
      onOpenFileNewTab: file => {
        console.info("onOpenFileNewTab", file);
      }
    },
    activePathName = activePath.match(/\/?([^/]*)\/$/)[1],
    rootPathIsActive = activePath === "/",
    filteredFileList = fileList
      .filter(file => file.path === activePath)
      .sort((a, b) => a.name.localeCompare(b.name)),
    recentFiles = fileList
      .filter(file => !file.isFolder)
      .sort((a, b) => b.lastAccessed - a.lastAccessed)
      .slice(0, 5),
    handleNewFile = () => {
      const name = findNewFilename("Untitled file", filteredFileList),
        newFile = { path: activePath, name };
      DefaultFileSystem.newFile(newFile);
      setFileList(fileList => fileList.concat(newFile));
      filePanelProps.onSelectFile(newFile);
    },
    handleNewFolder = () => {
      let name = findNewFilename("Untitled folder", filteredFileList);
      setFileList(fileList =>
        fileList.concat({ path: activePath, name, isFolder: true })
      );
    };
  return (
    <>
      <Panel
        title={
          <>
            <div className="flex-fill">My Files</div>
            <Dropdown drop="right" className="d-flex">
              <Dropdown.Toggle variant="circle" className="zerowidth">
                <FontAwesomeIcon icon={faPlusCircle} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                popperConfig={{
                  modifiers: {
                    preventOverflow: { boundariesElement: "viewport" }
                  }
                }}
              >
                <Dropdown.Item onSelect={handleNewFile}>
                  <FontAwesomeIcon fixedWidth icon={faFile} />
                  New File
                </Dropdown.Item>
                <Dropdown.Item onSelect={handleNewFolder}>
                  <FontAwesomeIcon fixedWidth icon={faFolder} />
                  New Folder
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </>
        }
        className="border-right"
      >
        {rootPathIsActive && (
          <FilePanelSection
            title="Recent Files"
            files={recentFiles}
            {...filePanelProps}
          />
        )}
        <FilePanelSection
          title={
            rootPathIsActive ? (
              "All Files"
            ) : (
              <>
                <FontAwesomeIcon icon={faCaretLeft} /> {activePathName}
              </>
            )
          }
          files={filteredFileList}
          {...filePanelProps}
          {...(rootPathIsActive ? {} : { onClickTitle: selectParentPath })}
        />
      </Panel>
      {undoToast && <ToastWithUndo {...undoToast} />}
      <Modal
        size="sm"
        show={renameFileModalState.isShowing}
        onHide={handleRenameFileModalClose}
        onEntered={() => renameTextControlRef.current.select()}
      >
        <Modal.Header closeButton>
          <Modal.Title>Rename</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please enter a new name</p>
          <Form.Control
            type="text"
            ref={renameTextControlRef}
            value={renameFileModalState.fileName}
            onChange={e =>
              setRenameFileModalState(state => ({
                ...state,
                fileName: e.target.value
              }))
            }
            onKeyDown={e => {
              if (e.key === "Enter") handleRenameFile();
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={handleRenameFileModalClose}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRenameFile}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FileManagementPanel;
